/**
 * SdpLevelBadge — pill showing a Structural Duality Principle's level.
 *
 * Level 1 SDPs come from 1-categorical adjunction pairs (the three DPGs);
 * level 2 SDPs come from higher-categorical adjunctions for singleton
 * NTs. The level is a first-class fact about an SDP, so every SDP
 * explainer surfaces it as a badge rather than burying it in prose.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { withAlpha } from "../../design-system/tokens.js";

export interface SdpLevelBadgeProps {
  readonly level: 1 | 2;
  /** SDP accent color (hex). */
  readonly accent: string;
}

export const SdpLevelBadge = React.memo(function SdpLevelBadge({
  level,
  accent,
}: SdpLevelBadgeProps): React.JSX.Element {
  const detail =
    level === 1 ? "1-categorical adjunction pair" : "higher-categorical (graded) adjunction";
  return (
    <span
      data-csk-sdp-level={level}
      aria-label={`SDP level ${level}: ${detail}`}
      title={detail}
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: "0.375rem",
        padding: "0.2rem 0.6rem",
        borderRadius: "9999px",
        backgroundColor: withAlpha(accent, "1A"),
        color: accent,
        border: `1px solid ${withAlpha(accent, "33")}`,
        fontSize: "0.6875rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        whiteSpace: "nowrap",
      }}
    >
      <span>{`Level ${level}`}</span>
      <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>{detail}</span>
    </span>
  );
});
